import {assertAndContinue} from 'Asserts';
import {getStore} from 'redux-stores/Store';

import {decryptMessageIfNeeded} from './decryptMessage';
import {fetchRealtimeEncryptionKeys} from './fetchRealtimeEncryptionKeys';
import {EncryptedRealtimeMessage, RealtimeMessage} from './RealtimeChannel';
import {RealtimeEncryptionKey} from './RealtimeEncryptionReduxActions';

/**
 * Decrypts a realtime message using the current encryption keys.
 * Handles key fetching, caching, and retry logic internally.
 *
 * NOTE: This is not a hook such that it can be used both in a React component and outside of one.
 */
export default async function decryptRealtimeMessage(
  channelName: string,
  message: RealtimeMessage | EncryptedRealtimeMessage
): Promise<RealtimeMessage> {
  const state = getStore().getState();
  const channelKeys = state.ui.realtimeEncryption.keysByChannel[channelName];
  const tryDecryption = (keys: Array<RealtimeEncryptionKey>): Promise<RealtimeMessage> =>
    decryptMessageIfNeeded(channelName, message, keys);

  // Get current keys from Redux state
  let keys = [channelKeys?.pastKey, channelKeys?.currentKey, channelKeys?.nextKey].filter((k) => k != null);

  // If no keys available for this channel, fetch them first
  if (keys.length === 0) {
    const {pastKey, currentKey, nextKey} = await fetchRealtimeEncryptionKeys(channelName);
    keys = [pastKey, currentKey, nextKey];
  }

  try {
    // Try decryption with current keys
    return await tryDecryption(keys);
  } catch (err) {
    // Refresh encryption keys and try again
    try {
      const {pastKey, currentKey, nextKey} = await fetchRealtimeEncryptionKeys(channelName);
      const refreshedKeys = [pastKey, currentKey, nextKey];
      return await tryDecryption(refreshedKeys);
    } catch (retryErr) {
      assertAndContinue(false, `Error decrypting message after key refresh: ${message.name}`, retryErr);
      throw retryErr;
    }
  }
}
